import React from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceLayout from '../../components/ServiceLayout';
import { useMetrics } from '../../hooks/useMetrics';
import { 
  BoltIcon,
  ShieldCheckIcon,
  CloudIcon,
  CogIcon,
  ChartBarIcon,
  ClockIcon,
  ServerIcon,
  DevicePhoneMobileIcon,
  WifiIcon
} from '@heroicons/react/24/outline';

const ManagedIT: React.FC = () => {
  const navigate = useNavigate();
  const { metrics, loading } = useMetrics();

  const features = [
    {
      icon: <ShieldCheckIcon />,
      title: '24/7 IT Support & Monitoring',
      description: 'Round-the-clock monitoring and support to ensure your systems are always running optimally.',
      benefits: [
        'Proactive system monitoring',
        '24/7 helpdesk support',
        'Remote troubleshooting',
        'Preventive maintenance',
        'Emergency response protocols'
      ]
    },
    {
      icon: <CloudIcon />,
      title: 'Cloud Infrastructure Management',
      description: 'Complete cloud strategy, migration, and ongoing management for maximum efficiency.',
      benefits: [
        'Cloud migration planning',
        'Multi-cloud management',
        'Cost optimization',
        'Security compliance',
        'Disaster recovery'
      ]
    },
    {
      icon: <ServerIcon />,
      title: 'Network & Server Administration',
      description: 'Expert management of your network infrastructure and server environments.',
      benefits: [
        'Network design and optimization',
        'Server maintenance and updates',
        'Performance monitoring',
        'Security patch management',
        'Backup and recovery'
      ]
    },
    {
      icon: <ChartBarIcon />,
      title: 'IT Strategy & Planning',
      description: 'Strategic IT consulting to align technology with your business objectives.',
      benefits: [
        'Technology roadmap development',
        'Budget planning and forecasting',
        'Vendor management',
        'Compliance consulting',
        'Digital transformation guidance'
      ]
    },
    {
      icon: <DevicePhoneMobileIcon />,
      title: 'Device & Endpoint Management',
      description: 'Comprehensive management of all devices and endpoints across your organization.',
      benefits: [
        'Device deployment and setup',
        'Mobile device management',
        'Software deployment',
        'Security policy enforcement',
        'Asset lifecycle management'
      ]
    },
    {
      icon: <WifiIcon />,
      title: 'Cybersecurity Services',
      description: 'Advanced security measures to protect your business from evolving cyber threats.',
      benefits: [
        'Security assessments',
        'Threat detection and response',
        'Employee security training',
        'Compliance management',
        'Incident response planning'
      ]
    }
  ];

  const process = {
    title: 'Our Managed IT Process',
    description: 'A structured approach that ensures seamless IT operations and continuous improvement.',
    steps: [
      {
        title: 'IT Assessment & Discovery',
        description: 'Comprehensive evaluation of your current IT infrastructure, security posture, and business requirements to create a baseline for improvement.'
      },
      {
        title: 'Strategic Planning & Design',
        description: 'Develop a customized IT strategy and architecture that aligns with your business goals and growth plans.'
      },
      {
        title: 'Implementation & Migration',
        description: 'Carefully execute the transition to managed services with minimal disruption to your business operations.'
      },
      {
        title: 'Ongoing Management & Support',
        description: 'Provide continuous monitoring, maintenance, and support to ensure optimal performance and reliability.'
      },
      {
        title: 'Optimization & Innovation',
        description: 'Regularly review and optimize your IT environment while introducing new technologies to maintain competitive advantage.'
      }
    ]
  };

  const stats = [
    { value: '365', label: 'Days Per Year Service' },
    { value: '24/7', label: 'Monitoring' },
    { value: metrics?.response_time_display || '<15min', label: 'Response Time' }
  ];

  const cta = {
    title: 'Ready for Worry-Free IT Management?',
    description: 'Let us handle your IT so you can focus on what matters most - growing your business.',
    buttonText: 'Get Started Today',
    onAction: () => navigate('/contact')
  };

  return (
    <ServiceLayout
      serviceName="Managed IT"
      icon={<BoltIcon />}
      title="Complete IT Management & Support"
      subtitle="Focus on your business while we handle your technology. Our comprehensive managed IT services provide 24/7 monitoring, support, and strategic guidance to keep your systems running smoothly and securely."
      heroImage="/images/services/managed-it-noc.webp"
      stats={stats}
      features={features}
      process={process}
      cta={cta}
    />
  );
};

export default ManagedIT;